import React, { useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Table, TableColumnConfig, Text, TextLink } from '@weave/design-system';
import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  CriterionExceptionType,
} from '../../handoff-checklist-v2.types';
import { getStatusIcon } from '../../utils/status-icon';
import { ExceptionCell } from './exception-cell';

interface MetricAccordionDetailProps {
  criterias: ChecklistMetricCriteria[];
  metricId: string;
  allowExceptions?: boolean;
  showWhereToFix?: boolean;
  readOnly?: boolean;
  onExceptionChange?: (
    metricId: string,
    criteriaIndex: number,
    exception: string
  ) => void;
}

type MetricAccordionDetailRow = {
  criteria: string;
  statusText: string;
  status: ChecklistMetricStatus;
  whereToFix:
    | {
        label: string;
        url: string;
      }
    | undefined;
  exception: string;
  exceptionOptions?: string[];
  exceptionType?: CriterionExceptionType;
  index: number;
  messageNode?: React.ReactNode;
};

type MetricAccordionDetailColumn = TableColumnConfig<MetricAccordionDetailRow> & {
  disableSortBy?: boolean;
};

export const MetricTable = ({
  criterias,
  metricId,
  allowExceptions = false,
  showWhereToFix = true,
  readOnly = false,
  onExceptionChange,
}: MetricAccordionDetailProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const columns: MetricAccordionDetailColumn[] = [
    {
      Header: 'Criteria',
      accessor: (row) => row,
      id: 'criteria',
      cellRenderer: (row) => (
        <Text size="medium" as="div">
          {row.criteria}
        </Text>
      ),
      disableSortBy: true,
    },
    {
      Header: 'Status/Reason',
      accessor: (row) => row,
      id: 'status',
      cellRenderer: (row) => {
        const StatusIcon = getStatusIcon(row.status);
        return (
          <div css={styles.statusCell}>
            {row.messageNode ?? (
              <>
                {StatusIcon && <StatusIcon css={styles.statusSvg} />}
                <Text
                  size="medium"
                  as="div"
                  css={
                    row.status !== ChecklistMetricStatus.PASSED
                      ? { fontWeight: 'bold' }
                      : undefined
                  }
                >
                  {row.statusText}
                </Text>
              </>
            )}
          </div>
        );
      },
      disableSortBy: true,
    },
    {
      Header: 'Where to Fix',
      accessor: (row) => row,
      id: 'whereToFix',
      omit: !showWhereToFix,
      cellRenderer: (row) =>
        row.status !== ChecklistMetricStatus.PASSED && row.whereToFix ? (
          <TextLink
            href={row.whereToFix.url}
            rel="noopener noreferrer"
            target="_blank"
            css={styles.fixLink}
          >
            {row.whereToFix.label}
          </TextLink>
        ) : (
          <Text size="small" color="light" as="div">
            —
          </Text>
        ),
      disableSortBy: true,
      maxWidth: 200,
    },
    {
      Header: 'Exception',
      accessor: (row) => row,
      id: 'exception',
      omit: !allowExceptions,
      cellRenderer: (row) => (
        <ExceptionCell
          exception={row.exception}
          metricId={metricId}
          criteriaIndex={row.index}
          isEditing={editingIndex === row.index}
          readOnly={readOnly}
          allowException={
            row.status !== ChecklistMetricStatus.PASSED &&
            row.exceptionType !== undefined &&
            row.exceptionType !== 'none'
          }
          exceptionOptions={row.exceptionOptions}
          exceptionType={row.exceptionType}
          onStartEdit={() => setEditingIndex(row.index)}
          onStopEdit={() => setEditingIndex(null)}
          onExceptionChange={onExceptionChange}
        />
      ),
      disableSortBy: true,
      maxWidth: 350,
    },
  ];

  const data: MetricAccordionDetailRow[] = criterias.map((c, idx) => ({
    criteria: c.description,
    statusText: c.status === ChecklistMetricStatus.PASSED ? 'Passed' : c.message || '-',
    status: c.status,
    whereToFix: c.whereToFix,
    exception: c.exception || '',
    exceptionOptions: c.exceptionOptions,
    exceptionType: c.exceptionType,
    index: idx,
    messageNode: c.messageNode,
  }));

  return (
    <div css={styles.detailContainer}>
      <Table
        colConfig={columns}
        data={data}
        hasResponsiveColWidths
        tableStyle={styles.table}
      />
    </div>
  );
};

const styles = {
  detailContainer: css`
    background-color: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
  `,
  table: css`
    margin: 0;

    [role='columnheader'] {
      background-color: #f4f5f7;
      color: #8f95a3;
      font-size: 14px;
      font-weight: 700;
      padding-bottom: ${theme.spacing(2)};
      border-bottom: 0;
    }

    [role='cell'] {
      padding-top: ${theme.spacing(2.5)};
      padding-bottom: ${theme.spacing(2.5)};
      vertical-align: top;
      border-bottom: 0;
    }

    [role='row'] {
      border-bottom: 0;
    }
  `,
  statusCell: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `,
  statusSvg: css`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  `,
  fixLink: css`
    font-weight: 600;
    font-size: 14px;
  `,
};
